import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Code,
  FileText,
  Maximize2,
  Minimize2,
  Play,
  Terminal,
  XCircle,
  CheckCircle2
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import TestHeader from '../components/Features/PracticeTest/TestHeader';
import {
  resetCompleteState,
  resetState,
  setIsStartTest
} from '../redux/Slices/QuestionsSlice';
import {
  resetTestSettings,
  setCurrentSectionIndex,
  setResetSatrtFullTest
} from '../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import {
  useCompileProgramMutation,
  useGetCategoryQuestionDataMutation,
  useGetCodeingEvaluationMutation,
  useGetCodingCategoriesQuery,
  useLazyGetCategoryQuestionsQuery,
  useSubmitCategoryQuestionMutation
} from '../services/startTestAPI/startTestApi';
import { renderHtmlWithTeX } from '../utils/RenderQuestionWithTex';

import EditorPage from './EditorPage';

type Category = {
  id: string;
  isActive: boolean;
  createdDate: string;
  updatedDate: string;
  sortOrder: number;
  categoryName: string;
};

type Question = {
  id: string;
  isUserAttempted: boolean;
  questionText: string;
};

type ProgrammingQuestion = {
  id: string;
  isActive: boolean;
  createdDate: string;
  questionTypeId: string;
  roundId: string;
  sectionId: string;
  topicId: string;
  subTopic: string;
  questionText: string;
  explanation: string | null;
  isProgrammingQuestion: boolean;
  options: string[] | null;
  sourceNameId: string;
  correctAnswer: string | null;
  questionStatus: number;
  categoryId: string[];
};

type UserTestQuestion = {
  id: string;
  isActive: boolean;
  userId: string;
  categoryId: string;
  questionId: string;
  testName: string;
  createdDate: string;
  updatedDate: string;
  question: ProgrammingQuestion;
  answers: string[] | null;
  testStatus: 'STARTED' | 'COMPLETED' | 'NOT_STARTED' | string; // extendable enum
};

export const CompilerOutput = ({
  result
}: {
  result: {
    success: boolean;
    output: string;
    error: string;
  } | null;
}) => {
  if (!result) return null;

  const isSuccess = result.success;

  return (
    <div className='h-[21vh] rounded-lg border'>
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-4 py-3 ${
          isSuccess
            ? 'border-b border-green-200 bg-green-50'
            : 'border-b border-red-200 bg-red-50'
        }`}
      >
        <Terminal
          className={`h-5 w-5 ${isSuccess ? 'text-green-600' : 'text-red-600'}`}
        />
        <span
          className={`font-semibold ${isSuccess ? 'text-green-800' : 'text-red-800'}`}
        >
          {isSuccess ? 'Compilation Successful' : 'Compilation Failed'}
        </span>
        {isSuccess ? (
          <CheckCircle className='ml-auto h-5 w-5 text-green-600' />
        ) : (
          <XCircle className='ml-auto h-5 w-5 text-red-600' />
        )}
      </div>

      {/* Output Content */}
      <div
        className='overflow-auto rounded-b-lg bg-gray-900 p-4 font-mono text-sm text-gray-100'
        style={{ height: 'calc(100% - 49px)' }}
      >
        {isSuccess ? (
          <div>
            <div className='mb-2 text-green-400'>Output:</div>
            <pre className='whitespace-pre-wrap'>{result.output}</pre>
          </div>
        ) : (
          <div>
            <div className='mb-2 text-red-400'>Error:</div>
            <pre className='whitespace-pre-wrap text-red-300'>
              {result.error}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

const ProgrammingCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [questionsData, setQuestionsData] = useState<Question[]>([]);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compileResult, setCompileResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [question, setQuestion] = useState<UserTestQuestion | null>(null);
  const [result, setResult] = useState<any>(null);

  const { isStartTest } = useAppSelector(state => state.root.questions);
  const {
    roundName,
    currentQuestionIndex,
    currentSectionName,
    allCodeingQuestions,
    selectedCodeingLanguage,
    currentCodeingQuestionAnswer
  } = useAppSelector(state => state.root.startFulltest);

  const { data: categoriesData, isLoading } = useGetCodingCategoriesQuery({});
  const [getCategoryQuestions] = useLazyGetCategoryQuestionsQuery();
  const [compileProgram] = useCompileProgramMutation();
  const [evaluateCode] = useGetCodeingEvaluationMutation();
  const [getCategoryQuestionData] = useGetCategoryQuestionDataMutation();
  const [submitCategoryQuestion] = useSubmitCategoryQuestionMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [maximizedPanel, setMaximizedPanel] = useState<
    'description' | 'code' | null
  >(null);

  const handleCategoryClick = async (category: string) => {
    setActiveCategory(category);
    const categoryId = categoriesData?.data?.find(
      (cat: Category) => cat.categoryName === category
    )?.id;
    try {
      setLoading(true);
      const res = await getCategoryQuestions({ categoryId }).unwrap();
      if (res?.data) {
        setQuestionsData(res?.data);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuestionClick = async (questionId: string) => {
    const categoryId = categoriesData?.data?.find(
      (cat: Category) => cat.categoryName === activeCategory
    )?.id;
    if (questionId && categoryId) {
      try {
        setLoading(true);
        const payload = {
          questionId,
          categoryId
        };
        const res = await getCategoryQuestionData(payload).unwrap();
        if (res?.data) {
          setQuestion(res?.data);
          dispatch(setIsStartTest(true));
          setLoading(false);
        }
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    }
  };

  const evaluateCodeingQuestions = async () => {
    try {
      let finalAnswer: any = null;

      try {
        const payload = {
          questionId: question?.questionId,
          sectionId: '',
          question: question?.question?.questionText,
          language: selectedCodeingLanguage,
          userProvidedSoluation: currentCodeingQuestionAnswer
        };
        const evaluatedCodeResponse = await evaluateCode(payload);

        finalAnswer = {
          questionId: payload?.questionId,
          userSelectedOption: '',
          isCorrectAnswer:
            evaluatedCodeResponse.data?.data?.isProvidedAnswerCorrect,
          actualCorrectOption: '',
          userSubmittedCode: payload.userProvidedSoluation,
          languageUsed: payload.language,
          aiGeneratedAnswer: evaluatedCodeResponse.data?.data?.idealAnswer,
          explanation: evaluatedCodeResponse.data?.data?.whatWentWrong
        };

        // overwrite, so only latest one stays
      } catch (error) {
        console.error('Error evaluating code:', error);
        toast.error('Error evaluating code submission');
        throw error;
      }

      return { answers: finalAnswer };
    } catch (error) {
      console.error('Error in evaluateCodeingQuestions:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true); // Start submission loading
    try {
      dispatch(resetState());
      const finalAnswers = await evaluateCodeingQuestions();

      const saveResponse = await submitCategoryQuestion({
        ...(question ?? {}),
        testStatus: 'COMPLETED',
        answers: finalAnswers.answers,
        question: {
          ...(question?.question ?? {})
        }
      });

      if (saveResponse.data.status === 'success') {
        toast.success('Test submitted successfully');
        dispatch(setIsStartTest(false));
        dispatch(setCurrentSectionIndex(0));
        dispatch(resetCompleteState());
        dispatch(setResetSatrtFullTest());
        dispatch(resetTestSettings());
        setQuestion(null);
        handleCategoryClick(activeCategory ?? '');
        navigate(
          `/programming-category-results/${question?.categoryId}/${question?.questionId}`
        );
      } else {
        toast.error(saveResponse.data.message || 'Error saving test');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error('Error submitting test. Please try again.');
    } finally {
      setIsSubmitting(false); // Stop submission loading
    }
  };

  const handleResultView = (questionId: string) => {
    dispatch(setIsStartTest(false));
    const categoryId = categoriesData?.data?.find(
      (cat: Category) => cat.categoryName === activeCategory
    )?.id;
    navigate(`/programming-category-results/${categoryId}/${questionId}`);
  };

  const displayRun =
    selectedCodeingLanguage === 'java' && currentCodeingQuestionAnswer;

  useEffect(() => {
    if (!categoriesData?.data) return;

    const sorted = [...categoriesData.data].sort(
      (a: Category, b: Category) => a.sortOrder - b.sortOrder
    );

    setCategories(sorted);

    if (sorted.length > 0) {
      handleCategoryClick(sorted[0].categoryName);
    }
  }, [categoriesData, isLoading]);

  if (isSubmitting) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'>
        <div className='mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl'>
          <div className='mx-auto mb-6 h-16 w-16 animate-spin rounded-full border-b-2 border-indigo-600'></div>
          <h2 className='mb-4 text-2xl font-bold text-gray-800'>
            Submitting Test...
          </h2>
          <p className='mb-6 text-gray-600'>
            Please wait while we evaluate your code and save your results.
          </p>
          <button
            disabled={true}
            className='w-full cursor-not-allowed rounded-2xl bg-gray-200 px-6 py-4 text-lg font-semibold text-gray-500'
          >
            View Results
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='w-full p-6'>
        {isStartTest && question?.questionId ? (
          <>
            <TestHeader
              title={'Coding Assessment'}
              time='0:00'
              currentSectionName={''}
              shouldShowTimer={false}
              closeFunction={() => {
                dispatch(setIsStartTest(false));
                setQuestion(null);
              }}
            />
            <div className='h-[2px] w-full bg-primary' />
            <div className='flex w-full items-center justify-center gap-[2px] px-6 py-3'>
              <button
                className={`rounded-l-lg bg-primary px-3 py-1.5 text-white ${isCompiling || !displayRun ? 'bg-gray-400' : 'bg-primary'}`}
                onClick={async () => {
                  setIsCompiling(true);
                  try {
                    const res = await compileProgram({
                      language: selectedCodeingLanguage?.toUpperCase(),
                      code: currentCodeingQuestionAnswer
                    }).unwrap();
                    setCompileResult(res);
                    setIsCompiling(false);
                  } catch (error) {
                    setIsCompiling(false);
                    setCompileResult((error as any)?.data);
                  }
                }}
              >
                <Play strokeWidth={1.5} />
              </button>
              <button
                className='rounded-r-lg bg-primary px-3 py-1.5 font-semibold text-white'
                disabled={false}
                onClick={() => {
                  handleSubmit();
                }}
              >
                Submit
              </button>
            </div>
            <div className='relative flex w-full flex-col justify-center gap-4 px-2 md:px-4 lg:flex-row'>
              <div
                className={`rounded-lg border border-primary transition-all duration-300 lg:h-[67vh] lg:overflow-y-auto ${maximizedPanel === 'code' ? 'lg:w-[4%]' : ''} ${maximizedPanel === 'description' ? 'lg:w-[96%]' : ''} ${!maximizedPanel ? 'lg:w-1/2' : ''} w-full`}
              >
                <div
                  className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
                    maximizedPanel === 'code'
                      ? 'h-full flex-col justify-start'
                      : 'flex-row justify-between'
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 ${maximizedPanel === 'code' ? 'flex-col' : 'flex-row'}`}
                  >
                    {!(maximizedPanel === 'code') && (
                      <FileText className='h-4 w-4 text-black' />
                    )}
                    {maximizedPanel === 'code' && (
                      <ChevronRight
                        className='mt-2 h-6 w-6 cursor-pointer text-black'
                        onClick={() => setMaximizedPanel(null)}
                      />
                    )}
                    <span
                      className={`text-lg font-normal text-black ${
                        maximizedPanel === 'code'
                          ? 'rotate-180 [writing-mode:vertical-rl]'
                          : ''
                      }`}
                    >
                      Description
                    </span>
                  </div>
                  <div
                    className={`hidden lg:block ${maximizedPanel === 'code' ? 'hidden' : ''}`}
                  >
                    {maximizedPanel === 'description' && (
                      <Minimize2
                        className='h-4 w-4 cursor-pointer'
                        onClick={() => setMaximizedPanel(null)}
                      />
                    )}
                    {maximizedPanel === null && (
                      <Maximize2
                        className='h-4 w-4 cursor-pointer'
                        onClick={() => setMaximizedPanel('description')}
                      />
                    )}
                  </div>
                </div>
                <div
                  className={`bg-white px-2 py-6 font-medium text-black lg:px-6 ${maximizedPanel === 'code' ? 'hidden' : 'block'}`}
                  dangerouslySetInnerHTML={{
                    __html: question?.question?.questionText ?? ''
                  }}
                />
              </div>

              <div
                className={`rounded-lg border border-primary transition-all duration-300 lg:overflow-y-auto ${maximizedPanel === 'description' ? 'lg:w-[4%]' : ''} ${maximizedPanel === 'code' ? 'lg:w-[96%]' : ''} ${!maximizedPanel ? 'lg:w-1/2' : ''} ${compileResult && (!maximizedPanel || maximizedPanel === 'code') ? 'lg:h-[45vh]' : 'lg:h-[67vh]'} mb-6 w-full lg:mb-0`}
              >
                <div
                  className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
                    maximizedPanel === 'description'
                      ? 'h-full flex-col justify-start'
                      : 'flex-row justify-between'
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 ${maximizedPanel === 'description' ? 'flex-col' : 'flex-row'}`}
                  >
                    {!(maximizedPanel === 'description') && (
                      <Code className='h-4 w-4 text-black' />
                    )}

                    {maximizedPanel === 'description' && (
                      <ChevronLeft
                        className='mt-2 h-6 w-6 cursor-pointer text-black'
                        onClick={() => setMaximizedPanel(null)}
                      />
                    )}

                    <span
                      className={`text-lg font-normal text-black ${
                        maximizedPanel === 'description'
                          ? 'rotate-180 [writing-mode:vertical-rl]'
                          : ''
                      }`}
                    >
                      Code
                    </span>
                  </div>
                  <div
                    className={`hidden lg:block ${maximizedPanel === 'description' ? 'hidden' : ''}`}
                  >
                    {maximizedPanel === 'code' && (
                      <Minimize2
                        className='h-4 w-4 cursor-pointer'
                        onClick={() => setMaximizedPanel(null)}
                      />
                    )}
                    {maximizedPanel === null && (
                      <Maximize2
                        className='h-4 w-4 cursor-pointer'
                        onClick={() => setMaximizedPanel('code')}
                      />
                    )}
                  </div>
                </div>
                <div
                  className={`mx-auto w-full ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
                >
                  <EditorPage
                    questionNumber={0}
                    questionText={question?.question?.questionText}
                    questionId={question?.questionId}
                    sectionId={''}
                    compileResult={compileResult}
                  />
                </div>
              </div>
              <div
                className={`absolute -bottom-1 right-1 px-3 py-2 ${maximizedPanel === 'code' ? 'w-[calc(100%-4%-22px)]' : 'w-1/2'} ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
              >
                <CompilerOutput result={compileResult} />
              </div>
            </div>
          </>
        ) : (
          <div className='w-full rounded-xl p-5 text-white shadow-lg'>
            {/* CATEGORY BUTTONS */}
            <div className='flex flex-wrap gap-3'>
              {categories?.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.categoryName)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat.categoryName
                      ? 'border-primary bg-primary text-white'
                      : 'border-primary text-primary hover:bg-primary/10'
                  }`}
                >
                  {cat.categoryName}
                </button>
              ))}
            </div>

            {/* DIVIDER */}
            <div className='my-5 h-[2px] w-full rounded-full bg-primary' />

            {/* QUESTION LIST */}
            {activeCategory && (
              <div>
                <h3 className='mb-4 text-lg font-semibold text-primary'>
                  {activeCategory} Coding Questions
                </h3>

                <div className='flex flex-col gap-3'>
                  {questionsData.length > 0 ? (
                    questionsData.map((q, index) => (
                      <div
                        key={q?.id}
                        className='hover:bg-gray-750 flex cursor-pointer items-start justify-between rounded-lg border border-primary p-4 transition'
                      >
                        <div onClick={() => handleQuestionClick(q?.id)}>
                          <p className='text-sm text-black'>
                            <span className='mr-2 font-semibold'>
                              {index + 1}.
                            </span>
                            {renderHtmlWithTeX(q.questionText, true)}
                          </p>
                        </div>
                        <div className='mt-auto'>
                          {q?.isUserAttempted && (
                            <div className='flex items-center gap-2'>
                              <CheckCircle2 className='ml-auto h-5 w-5 text-green-600' />
                              <button
                                className='w-fit whitespace-nowrap rounded-full bg-green-600 px-3 py-1 text-xs font-medium'
                                onClick={() => handleResultView(q?.id)}
                              >
                                View Result
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className='text-gray-400'>
                      No questions found for this category.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProgrammingCategories;
